import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import { useAuthContext } from "../context/AuthContext";
import {GET_INVITATIONS} from "../graphql/invitationGraphql";
import { SnackBarContext } from './SnackBarContext';
import useGraphql from "../Hooks/useGraphql";
import useLog from "../Hooks/useLog";
import { getStorage } from '../auth/storage';

const InvitationContext = createContext();
const { Provider } = InvitationContext;

const readStoredIdentity = () => {
    const storage = getStorage();
    const firebaseUid = storage.getItem('firebaseUid');
    const impersonation = storage.getItem('impersonate');
    if (!firebaseUid) return null;
    try {
        return JSON.stringify([firebaseUid, Boolean(impersonation), JSON.parse(impersonation)?._id]);
    } catch (_) { return null; }
};

const InvitationProvider = ({ children }) => {
    const auth = useAuthContext();
    const snackBar = useContext(SnackBarContext);
    const [loadedInvitations, setLoadedInvitations] = useState({ context: null, items: [] });
    const {axiosGraphQL} = useGraphql()
    const {errorLog} = useLog();
    const sessionKey = auth.isAuthenticated() ? JSON.stringify([
        auth.firebaseUser?.uid, auth.authState.user._id, auth.authState.impersonate?._id,
    ]) : null;
    const activeContext = useRef(null);
    const latest = useRef(null);
    latest.current = { sessionKey, axiosGraphQL, errorLog, snackBar };

    const getInvitations = useCallback(async () => {
        const context = activeContext.current;
        const identity = readStoredIdentity();
        if (!context?.key || context.key !== latest.current.sessionKey || !identity) return;
        const requestId = ++context.requestId;
        const isCurrent = () => activeContext.current === context &&
            latest.current.sessionKey === context.key && context.requestId === requestId &&
            readStoredIdentity() === identity;
        try {
            const response = await latest.current.axiosGraphQL({
                query: GET_INVITATIONS,
                variables: {limit: 100},
            });
            // Cancelled refreshes resolve without data so callers do not start
            // follow-up requests using a different account's session.
            if (!isCurrent()) return;
            const items = response?.data?.data?.invitations?.filter(
                invitation => invitation?.status === 'pending',
            ) || [];
            setLoadedInvitations({ context, items });
            return items;
        } catch (error) {
            if (!isCurrent()) return;
            const { errorLog, snackBar } = latest.current;
            errorLog(error, 'InvitationContext', 'useEffect', 'GET_INVITATIONS');
            snackBar.setOptions({
                alertMessage: error.message,
                alertSeverity: "error",
                });
            snackBar.setSnackbarOpen(true);
        }
    }, []);

    useEffect(() => {
        const context = { key: sessionKey, requestId: 0 };
        activeContext.current = context;
        setLoadedInvitations({ context, items: [] });
        // Firebase credentials can exist while Core registration is pending.
        // Only resolved, authorized Manager sessions may load invitations.
        if (sessionKey) getInvitations();
        return () => { activeContext.current = null; };
    }, [sessionKey, getInvitations]);

    const invitations = sessionKey && loadedInvitations.context === activeContext.current &&
        loadedInvitations.context?.key === sessionKey ? loadedInvitations.items : [];

    return (
        <Provider value={{invitations, getInvitations}}>
            {children}
        </Provider>
    );
};

export const useInvitations = () => {
    return useContext(InvitationContext)
}


InvitationProvider.propTypes = {

};

export default InvitationProvider;
